import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import { Badge, cn } from '@simplywallst/ui-core/next';
import { DEMO_PORTFOLIO_ID } from '@/constants';
import { usePortfolio } from '@/hooks/portfolio/usePortfolio';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';
import { formatterPercentage } from '@/utilities/format';
import { getPath } from '@/router/utils';
import { ROUTE_PORTFOLIO_DEMO } from '@/constants/routes';
import { Card } from './Card';
import WarningIcon from '@simplywallst/ui-core/icons/WarningSolid';
import { Bones } from '../../Bones';
import { type ReactNode } from 'react';

const hairSpace = ' ';

export const Layout = ({
  isPositive,
  children,
}: {
  isPositive?: boolean;
  children: ReactNode;
}) => (
  <div className="relative h-full">
    {/* Large backgrounds */}
    <div className="pointer-events-none absolute inset-0 -right-x4 overflow-hidden max-md:hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_top_right,var(--tw-gradient-from)_50%,var(--tw-gradient-to))] from-transparent to-[rgba(255,255,255,.03)] light:to-[rgba(0,0,0,.05)]" />
      {isPositive !== undefined && (
        <div
          className={cn(
            'absolute size-full translate-y-1/2',
            'bg-[radial-gradient(var(--tw-gradient-from),var(--tw-gradient-to)_50%)] to-transparent',
            'animate-fade-in [animation-delay:.2s] [animation-fill-mode:backwards]',
            isPositive ? 'from-good-subtle' : 'from-bad-subtle'
          )}
        />
      )}
    </div>
    {/* Small backgrounds */}
    <div className="pointer-events-none absolute inset-0 md:hidden">
      <div className="border absolute inset-0 rounded-md border-subtle shadow light:shadow-sm" />
      {isPositive !== undefined ? (
        <div
          className={cn(
            'absolute inset-px rounded-md bg-legacy-noir/20 backdrop-blur-lg light:hidden',
            'bg-[linear-gradient(to_top_right,var(--tw-gradient-from),var(--tw-gradient-to)_45%)] to-transparent',
            'animate-fade-in [animation-delay:.2s] [animation-fill-mode:backwards]',
            isPositive ? 'from-good-subtle' : 'from-bad-subtle'
          )}
        />
      ) : (
        <div className="absolute inset-px rounded-md bg-p-black-100 opacity-5 backdrop-blur-lg light:hidden" />
      )}
    </div>

    <div className="relative flex h-full items-center justify-between gap-2 text-xs max-md:px-x2">
      {children}
    </div>
  </div>
);

export const BonesLayout = () => (
  <Layout>
    <Card>
      <Bones className="w-[9ch]" />
    </Card>
  </Layout>
);

export const IncompleteDataLayout = ({
  slug,
  totalCount,
  incompleteCount,
}: {
  slug: string;
  totalCount: number;
  incompleteCount: number;
}) => {
  const { trackEvent } = useTracking();
  return (
    <Layout>
      <Card slug={slug} count={totalCount}>
        <RouterLink
          to={slug}
          className="grid grid-cols-[auto_1fr] items-center gap-0.5 text-current no-underline focus-visible:ring"
          onClick={() => {
            trackEvent({
              type: 'track',
              action: 'click',
              modifier: 'missingData',
              subject: 'portfolioPosition',
            });
          }}
        >
          <WarningIcon className="-ml-x0_5 -mt-px inline-block size-5 fill-warning" />
          {/* TODO: i18n */}
          {incompleteCount && totalCount > 1
            ? `${incompleteCount} missing data`
            : 'Missing data'}
        </RouterLink>
      </Card>
    </Layout>
  );
};

export const DemoLayout = () => {
  const demo = usePortfolio({
    id: DEMO_PORTFOLIO_ID,
  });

  if (demo.status === 'pending') return <BonesLayout />;

  // Allow error boundary to catch error and display custom message
  if (demo.status === 'error')
    throw new Error(
      demo.error instanceof Error ? demo.error.message : String(demo.error)
    );

  const roiValue = formatCurrencyToScaleTruncated(demo.data?.analysis.roiValue);
  const roiPercentage = formatterPercentage(demo.data?.analysis.roi ?? 0);
  const isPositive = demo.data?.analysis.roi
    ? demo.data?.analysis.roi > 0
    : true;
  const slug = getPath({ type: ROUTE_PORTFOLIO_DEMO, payload: {} });

  return (
    <Layout isPositive={isPositive}>
      <Card slug={slug}>
        <span className="whitespace-nowrap">{roiValue}</span>
        <span
          className={cn(
            'flex whitespace-nowrap',
            isPositive ? 'text-good' : 'text-bad'
          )}
        >
          {isPositive ? '↑' : '↓'}
          {hairSpace}
          {roiPercentage}
        </span>
        <Badge size="small" className="min-w-fit">
          {/* TODO: i18n */}
          Demo
        </Badge>
      </Card>
    </Layout>
  );
};
